import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import {
  LogIn, LogOut, Download, Users, Gamepad2, Zap, Trophy,
  Search, Shield, Home, AlertCircle, Eye,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TK_KEY = "silicom-admin-token";
const EXP_KEY = "silicom-admin-exp";

const getToken = () => {
  const t = localStorage.getItem(TK_KEY);
  const exp = parseInt(localStorage.getItem(EXP_KEY) || "0", 10);
  if (!t || !exp || Date.now() > exp) {
    localStorage.removeItem(TK_KEY); localStorage.removeItem(EXP_KEY);
    return null;
  }
  return t;
};

const SEGMENTOS = [
  { key: "competitivos", label: "Competitivos" },
  { key: "streamers", label: "Streamers" },
  { key: "creadores", label: "Creadores" },
  { key: "equipos_clan", label: "Equipos / clanes" },
  { key: "torneos", label: "Torneos" },
  { key: "organizadores", label: "Organizadores" },
  { key: "discord_admins", label: "Admin de comunidades" },
  { key: "mas_de_10", label: "Juegan con +10 personas" },
];

const CANALES = ["whatsapp", "instagram", "facebook", "qr", "directo", "otros"];

const Login = ({ onOk }) => {
  const { toast } = useToast();
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await axios.post(`${API}/gaming/admin/login`, { username: u, password: p });
      localStorage.setItem(TK_KEY, r.data.token);
      localStorage.setItem(EXP_KEY, String(Date.now() + r.data.expires_in * 1000));
      onOk();
    } catch (err) {
      toast({
        title: "Error",
        description: err?.response?.data?.detail || "Credenciales inválidas",
        variant: "destructive",
      });
    } finally { setLoading(false); }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 p-6">
      <form onSubmit={submit} className="w-full max-w-sm rounded-3xl border border-white/10 bg-slate-900 p-8 text-white">
        <div className="mb-6 flex items-center gap-2">
          <Shield className="h-6 w-6 text-fuchsia-400" />
          <h1 className="text-xl font-black">Silicomlan Gaming · Admin</h1>
        </div>
        <Label className="text-slate-300">Usuario</Label>
        <Input data-testid="admin-user" value={u} onChange={e => setU(e.target.value)} className="mt-1 border-white/10 bg-white/5 text-white" autoFocus />
        <Label className="mt-4 block text-slate-300">Contraseña</Label>
        <Input data-testid="admin-pass" type="password" value={p} onChange={e => setP(e.target.value)} className="mt-1 border-white/10 bg-white/5 text-white" />
        <Button data-testid="admin-login" type="submit" disabled={loading} className="mt-6 w-full bg-gradient-to-r from-fuchsia-500 to-pink-500 font-bold text-white">
          <LogIn className="mr-2 h-4 w-4" /> {loading ? "..." : "Ingresar"}
        </Button>
      </form>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, color = "text-cyan-400", testid }) => (
  <div className="rounded-2xl border border-white/10 bg-slate-900 p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
      <Icon className={`h-4 w-4 ${color}`} /> {label}
    </div>
    <p className="mt-2 text-3xl font-black text-white">{value ?? 0}</p>
  </div>
);

const BarRow = ({ label, value, max }) => (
  <div className="mb-2">
    <div className="flex justify-between text-xs text-slate-300">
      <span className="truncate pr-2">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
    <div className="mt-1 h-2 overflow-hidden rounded-full bg-white/5">
      <div className="h-full bg-gradient-to-r from-fuchsia-500 to-cyan-400" style={{ width: `${max ? (value / max) * 100 : 0}%` }} />
    </div>
  </div>
);

const Dashboard = ({ onLogout }) => {
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [nivel, setNivel] = useState("");
  const [actividad, setActividad] = useState("");
  const [canal, setCanal] = useState("");
  const [segmento, setSegmento] = useState("");
  const [duplicados, setDuplicados] = useState("");
  const [elegible, setElegible] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [detail, setDetail] = useState(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${getToken()}` }), []);

  const loadAll = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/gaming/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/gaming/admin/responses`, {
          headers: authHeaders(),
          params: { q, nivel, actividad, canal, segmento, duplicados, elegible },
        }),
      ]);
      setStats(s.data);
      setRows(r.data.items);
      setPage(1);
    } catch (err) {
      if (err?.response?.status === 401) { onLogout(); return; }
      toast({ title: "Error cargando datos", variant: "destructive" });
    }
  }, [authHeaders, onLogout, toast, q, nivel, actividad, canal, segmento, duplicados, elegible]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const download = () => {
    const params = new URLSearchParams();
    if (canal) params.set("canal", canal);
    if (segmento) params.set("segmento", segmento);
    const url = `${API}/gaming/admin/export.csv?${params.toString()}`;
    fetch(url, { headers: authHeaders() })
      .then(r => r.blob())
      .then(b => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        const today = new Date().toISOString().slice(0, 10);
        a.download = `silicomlan-gaming-participantes-${today}.csv`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast({ title: "No se pudo exportar", variant: "destructive" }));
  };

  const openDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/gaming/admin/responses/${id}`, { headers: authHeaders() });
      setDetail(r.data);
    } catch { toast({ title: "No se pudo abrir la ficha", variant: "destructive" }); }
  };

  const t = stats?.totals || {};
  const bars = (obj) => {
    const entries = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
    const max = entries[0]?.[1] || 1;
    return entries.map(([k, v]) => <BarRow key={k} label={k} value={v} max={max} />);
  };

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="min-h-screen bg-slate-950 p-4 text-white md:p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-400">
              <Shield className="h-3.5 w-3.5 text-fuchsia-400" /> Panel administrativo
            </div>
            <h1 className="mt-1 text-2xl font-black">Silicomlan Gaming · Dashboard</h1>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <a href="/" data-testid="admin-back-home"><Home className="mr-2 h-4 w-4" /> Volver al sitio</a>
            </Button>
            <Button onClick={download} data-testid="admin-export" className="bg-cyan-500 hover:bg-cyan-600">
              <Download className="mr-2 h-4 w-4" /> Exportar CSV
            </Button>
            <Button onClick={onLogout} data-testid="admin-logout" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">
              <LogOut className="mr-2 h-4 w-4" /> Salir
            </Button>
          </div>
        </div>

        {/* Resumen */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon={Users} label="Visitas totales" value={t.visitas_totales} color="text-slate-200" testid="metric-visitas" />
          <Metric icon={Users} label="Formularios iniciados" value={t.formularios_iniciados} color="text-sky-300" testid="metric-iniciados" />
          <Metric icon={Trophy} label="Completados" value={t.formularios_completados} color="text-emerald-400" testid="metric-completados" />
          <Metric icon={Zap} label="Conversión %" value={t.tasa_conversion_pct} color="text-fuchsia-400" testid="metric-conversion" />
        </div>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Metric icon={Users} label="Visitas únicas" value={t.visitas_unicas} color="text-slate-200" />
          <Metric icon={Gamepad2} label="Clics 'Quiero ser parte'" value={t.clicks_participar} color="text-sky-300" />
          <Metric icon={Home} label="Hogares únicos" value={t.hogares_unicos} color="text-cyan-300" />
          <Metric icon={AlertCircle} label="Duplicados posibles" value={t.duplicados_posibles} color="text-amber-400" testid="metric-duplicados" />
        </div>

        {/* Funnel pasos */}
        <h2 className="mt-10 text-sm font-black uppercase tracking-widest text-fuchsia-400">Embudo del formulario</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-5">
          <Metric icon={Users} label="Iniciados" value={t.formularios_iniciados} color="text-slate-300" />
          <Metric icon={Users} label="Paso 1 OK" value={t.formularios_paso1} color="text-sky-300" />
          <Metric icon={Users} label="Paso 2 OK" value={t.formularios_paso2} color="text-sky-300" />
          <Metric icon={Users} label="Paso 3 OK" value={t.formularios_paso3} color="text-sky-300" />
          <Metric icon={Trophy} label="Completados" value={t.formularios_completados} color="text-emerald-400" />
        </div>

        {/* Segmentos clickeables */}
        <h2 className="mt-10 text-sm font-black uppercase tracking-widest text-fuchsia-400">Segmentos clave</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {SEGMENTOS.map((s) => (
            <button
              key={s.key}
              onClick={() => setSegmento(segmento === s.key ? "" : s.key)}
              data-testid={`admin-segment-${s.key}`}
              className={`rounded-xl border p-4 text-left transition-colors ${
                segmento === s.key ? "border-fuchsia-400 bg-fuchsia-500/10" : "border-white/10 bg-slate-900 hover:border-white/20"
              }`}
            >
              <p className="text-[10px] uppercase tracking-widest text-slate-400">{s.label}</p>
              <p className="mt-1 text-2xl font-black text-cyan-300">{stats?.segmentos?.[s.key] ?? 0}</p>
            </button>
          ))}
        </div>

        {/* Origen por canal */}
        <h2 className="mt-10 text-sm font-black uppercase tracking-widest text-fuchsia-400">Adquisición por canal</h2>
        <div className="mt-3 overflow-x-auto rounded-2xl border border-white/10 bg-slate-900">
          <table className="w-full text-sm">
            <thead className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-slate-400">
              <tr>
                <th className="py-2 pl-4 pr-3">Canal</th>
                <th className="py-2 pr-3">Visitas</th>
                <th className="py-2 pr-3">Iniciados</th>
                <th className="py-2 pr-3">Completados</th>
                <th className="py-2 pr-3">Conversión %</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {(stats?.origen_rows || []).map((row) => (
                <tr key={row.canal} className="border-b border-white/5 text-slate-200">
                  <td className="py-2 pl-4 pr-3 font-bold capitalize">{row.canal}</td>
                  <td className="py-2 pr-3">{row.visitas}</td>
                  <td className="py-2 pr-3">{row.iniciados}</td>
                  <td className="py-2 pr-3 text-emerald-300">{row.completados}</td>
                  <td className="py-2 pr-3 text-fuchsia-300">{row.conversion_pct}%</td>
                  <td className="py-2 pr-4">
                    <button
                      onClick={() => setCanal(canal === row.canal ? "" : row.canal)}
                      data-testid={`admin-canal-${row.canal}`}
                      className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-widest ${
                        canal === row.canal ? "border-fuchsia-400 bg-fuchsia-500/20 text-white" : "border-white/15 text-slate-300 hover:text-white"
                      }`}
                    >
                      {canal === row.canal ? "Quitar filtro" : "Filtrar"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Distribuciones */}
        <div className="mt-10 grid gap-6 lg:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Plataformas</p>{bars(stats?.plataformas)}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Edad</p>{bars(stats?.edad)}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Nivel</p>{bars(stats?.nivel)}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Actividades</p>{bars(stats?.actividades)}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Intereses futuros</p>{bars(stats?.intereses)}
          </div>
          <div className="rounded-2xl border border-white/10 bg-slate-900 p-5">
            <p className="mb-3 text-sm font-bold">Quién juega</p>{bars(stats?.quien_juega)}
          </div>
        </div>

        {/* Filtros + Tabla */}
        <div className="mt-10 rounded-3xl border border-white/10 bg-slate-900 p-5">
          <div className="mb-4 flex flex-wrap items-center gap-3">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input data-testid="admin-search" placeholder="Buscar nombre, whatsapp, juego, comunidad..." value={q} onChange={e => setQ(e.target.value)} className="border-white/10 bg-white/5 pl-9 text-white" />
            </div>
            <Select value={nivel || "all"} onValueChange={(v) => setNivel(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 border-white/10 bg-white/5 text-white"><SelectValue placeholder="Nivel" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-900 text-white">
                <SelectItem value="all">Todos los niveles</SelectItem>
                {["Juego de vez en cuando","Juego bastante seguido","Juego mucho","Juego competitivamente"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={actividad || "all"} onValueChange={(v) => setActividad(v === "all" ? "" : v)}>
              <SelectTrigger className="w-56 border-white/10 bg-white/5 text-white"><SelectValue placeholder="Actividad" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-900 text-white">
                <SelectItem value="all">Todas</SelectItem>
                {["Streaming","Equipo / clan","Torneos","Esports","Creación de contenido","Organizo torneos o comunidades","Discord u otra comunidad"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={canal || "all"} onValueChange={(v) => setCanal(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40 border-white/10 bg-white/5 text-white"><SelectValue placeholder="Canal" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-900 text-white">
                <SelectItem value="all">Todos los canales</SelectItem>
                {CANALES.map(x => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={elegible || "all"} onValueChange={(v) => setElegible(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 border-white/10 bg-white/5 text-white" data-testid="admin-filter-elegible"><SelectValue placeholder="Elegible sorteo" /></SelectTrigger>
              <SelectContent className="border-white/10 bg-slate-900 text-white">
                <SelectItem value="all">Elegible sorteo: todos</SelectItem>
                <SelectItem value="si">Elegible sorteo: Sí</SelectItem>
                <SelectItem value="no">Elegible sorteo: No</SelectItem>
              </SelectContent>
            </Select>
            <button
              onClick={() => setDuplicados(duplicados === "1" ? "" : "1")}
              data-testid="admin-filter-dup"
              className={`rounded-full border px-3 py-2 text-[11px] font-bold uppercase tracking-widest ${
                duplicados === "1" ? "border-amber-400 bg-amber-500/10 text-amber-200" : "border-white/15 text-slate-300 hover:text-white"
              }`}
            >
              {duplicados === "1" ? "Mostrando duplicados" : "Posibles duplicados"}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-left text-xs uppercase tracking-widest text-slate-400">
                <tr>
                  <th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3">Nombre</th>
                  <th className="py-2 pr-3">WhatsApp</th>
                  <th className="py-2 pr-3">Edad</th>
                  <th className="py-2 pr-3">Nivel</th>
                  <th className="py-2 pr-3">Plataformas</th>
                  <th className="py-2 pr-3">Actividades</th>
                  <th className="py-2 pr-3">Canal</th>
                  <th className="py-2 pr-3">Cliente</th>
                  <th className="py-2 pr-3"></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(r => (
                  <tr key={r.id} className={`border-b border-white/5 text-slate-200 ${r.is_duplicate_candidate ? "bg-amber-500/5" : ""}`} data-testid="admin-row">
                    <td className="py-2 pr-3 text-xs text-slate-400">{r.created_at?.slice(0,16).replace("T"," ")}</td>
                    <td className="py-2 pr-3 font-semibold text-white">
                      {r.nombre}
                      {r.is_duplicate_candidate && (
                        <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-amber-300">dup?</span>
                      )}
                    </td>
                    <td className="py-2 pr-3 text-cyan-300">{r.whatsapp}</td>
                    <td className="py-2 pr-3 text-xs">{r.edad_rango}</td>
                    <td className="py-2 pr-3 text-xs">{r.nivel}</td>
                    <td className="py-2 pr-3 text-xs">{(r.plataformas||[]).join(", ")}</td>
                    <td className="py-2 pr-3 text-xs">{(r.actividades||[]).join(", ")}</td>
                    <td className="py-2 pr-3 text-xs capitalize">{r.utm_source_canon || "directo"}</td>
                    <td className="py-2 pr-3 text-xs">
                      {r.es_cliente}
                      {r.es_cliente === "No" && (
                        <span className="ml-2 rounded-full bg-rose-500/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-rose-300" data-testid="admin-no-elegible-badge">No elegible sorteo</span>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      <button
                        onClick={() => openDetail(r.id)}
                        data-testid={`admin-view-${r.id}`}
                        className="inline-flex items-center gap-1 rounded-full border border-white/15 bg-white/5 px-2 py-1 text-[10px] font-bold uppercase tracking-widest text-slate-200 hover:bg-white/10"
                      >
                        <Eye className="h-3 w-3" /> Ver
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr><td colSpan={10} className="py-10 text-center text-slate-500">Sin respuestas todavía.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">
            <span>{rows.length} resultados</span>
            {totalPages > 1 && (
              <div className="flex gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-slate-200 disabled:opacity-50"
                >Anterior</button>
                <span className="px-2 py-1">Página {page} / {totalPages}</span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-slate-200 disabled:opacity-50"
                >Siguiente</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto border-white/10 bg-slate-900 text-white sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Ficha participante</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              <p><b>Nombre:</b> {detail.nombre}</p>
              <p><b>WhatsApp:</b> {detail.whatsapp} <span className="text-slate-400">({detail.whatsapp_normalized})</span></p>
              <p><b>Edad:</b> {detail.edad_rango}</p>
              <p><b>Quién juega:</b> {detail.quien_juega}</p>
              <p><b>Plataformas:</b> {(detail.plataformas||[]).join(", ")}</p>
              <p><b>Juegos:</b> {detail.juegos}</p>
              <p><b>Nivel:</b> {detail.nivel}</p>
              <p><b>Cantidad jugadores:</b> {detail.cantidad_jugadores}</p>
              <p><b>Actividades:</b> {(detail.actividades||[]).join(", ")}</p>
              <p><b>Comunidad:</b> {detail.comunidad_detalle || "—"}</p>
              <p><b>Intereses:</b> {(detail.intereses||[]).join(", ")}</p>
              <p><b>Cliente Silicomlan:</b> {detail.es_cliente}</p>
              <p><b>Elegible sorteo:</b> {detail.es_cliente === "No" ? "No" : "Sí"}</p>
              <p><b>Autorización tutor:</b> {detail.autorizacion_tutor ? "Sí" : "No/NA"}</p>
              <p><b>Origen:</b> {detail.utm_source_canon} ({detail.utm_source || "—"} / {detail.utm_medium || "—"} / {detail.utm_campaign || "—"})</p>
              <p><b>Referrer:</b> {detail.referrer || "—"}</p>
              <p><b>Fecha:</b> {detail.created_at}</p>
              <p><b>Duplicado posible:</b> {detail.is_duplicate_candidate ? "Sí" : "No"}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const GamingAdmin = () => {
  const [authed, setAuthed] = useState(!!getToken());
  useEffect(() => { document.title = "Admin · Silicomlan Gaming"; }, []);
  return authed
    ? <Dashboard onLogout={() => { localStorage.removeItem(TK_KEY); localStorage.removeItem(EXP_KEY); setAuthed(false); }} />
    : <Login onOk={() => setAuthed(true)} />;
};

export default GamingAdmin;
